"use client";

import { useState } from "react";
import { ModalShell } from "@/app/components/ui/modal-shell";
import { APP_VERSION, CHANGELOG, type ChangeType } from "@/lib/changelog";

const TYPE_META: Record<ChangeType, { label: string; cls: string }> = {
  feat: {
    label: "Nouveau",
    cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  fix: {
    label: "Correctif",
    cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  },
  improve: {
    label: "Amélioration",
    cls: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  },
};

function formatDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

/**
 * Modale « Notes de version » (changelog). Contrôlée via open/onClose pour
 * pouvoir être déclenchée depuis plusieurs endroits (sidebar desktop ET
 * menu hamburger mobile).
 */
export function ChangelogModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Notes de version"
      subtitle={`Gestic v${APP_VERSION} · nouveautés & corrections`}
      width="min(560px, 100%)"
      zIndex={130}
    >
      <div className="px-5 py-4 space-y-6">
        {CHANGELOG.map((entry) => {
          const date = formatDate(entry.date);
          return (
            <div key={entry.version} className="space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-sm font-bold text-forest dark:text-emerald-300">
                    v{entry.version}
                  </span>
                  {entry.title && (
                    <span className="text-sm font-medium truncate">{entry.title}</span>
                  )}
                </div>
                {entry.inProgress ? (
                  <span className="shrink-0 px-1.5 py-px rounded-full text-[9px] font-semibold uppercase tracking-wide bg-forest/15 text-forest dark:text-emerald-300">
                    En cours
                  </span>
                ) : date ? (
                  <span className="text-[11px] text-text-muted shrink-0 tabular-nums">{date}</span>
                ) : null}
              </div>
              <ul className="space-y-1.5">
                {entry.changes.map((c, i) => {
                  const meta = TYPE_META[c.type];
                  return (
                    <li key={i} className="flex gap-2 text-xs leading-relaxed">
                      <span
                        className={`shrink-0 mt-0.5 px-1.5 py-px rounded text-[9px] font-semibold uppercase tracking-wide h-fit ${meta.cls}`}
                      >
                        {meta.label}
                      </span>
                      <span className="text-text-secondary">{c.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </ModalShell>
  );
}

const SparklesIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
    />
  </svg>
);

/**
 * Étiquette de version (« Gestic v1.4 ») + bouton à sa droite qui ouvre la
 * modale « Notes de version ». Remplace le `<p>Gestic v1.0</p>` en bas de la
 * sidebar desktop.
 */
export function ChangelogButton() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1.5">
      <p className="text-xs text-text-muted pl-2">Gestic v{APP_VERSION}</p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Notes de version"
        aria-label="Notes de version"
        className="p-1 rounded-lg text-text-muted hover:text-forest hover:bg-forest/10 transition-colors"
      >
        <SparklesIcon className="w-3.5 h-3.5" />
      </button>
      <ChangelogModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

/**
 * Bouton « Notes de version » pour un menu (ex : hamburger mobile). Pleine
 * largeur, avec icône + n° de version. NE contient PAS la modale : celle-ci
 * doit être rendue par un parent toujours monté (sinon elle se ferme dès que
 * le menu se referme). Cf. ModuleSwitcher.
 */
export function ChangelogMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-text-secondary hover:bg-surface-secondary/40 hover:text-foreground transition-colors"
    >
      <SparklesIcon className="w-4 h-4 shrink-0" />
      <span>Notes de version</span>
      <span className="ml-auto text-[11px] text-text-muted tabular-nums">v{APP_VERSION}</span>
    </button>
  );
}
